import { createTheme, type ThemeOptions } from '@mui/material/styles';
import {
  darkTheme as MuiDarkTheme,
  theme as MuiLightTheme,
} from '@locus-taxy/encore-mui';

/**
 * Hybrid theme: Light mode base with dark sidebar/header
 * This theme provides a light content area with dark navigation elements
 */
export const createDashboardTheme = () => {
  return createTheme(MuiLightTheme as ThemeOptions, {
    components: {
      // Dark theme for Drawer (sidebar)
      MuiDrawer: {
        styleOverrides: {
          root: {
            '&:not(.MuiDrawer-docked)': {
              zIndex: 1300,
            },
            // Subtle custom scrollbar styling for a smoother UX
            '& .MuiDrawer-paper nav': {
              scrollbarColor: `${MuiDarkTheme.palette.divider} transparent`,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: 8,
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: MuiDarkTheme.palette.divider,
              },
            },
            // Only apply overrides to docked drawers
            '&.MuiDrawer-docked .MuiDrawer-paper': {
              backgroundColor: MuiDarkTheme.palette.background.paper,
              color: MuiDarkTheme.palette.text.primary,
              borderRight: 'none',
            },
            '&.MuiDrawer-docked': {
              // Apply dark theme colors to all sidebar content
              '& .MuiListItemButton-root': {
                // Inactive state: use secondary text color
                color: MuiDarkTheme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                '&:hover': {
                  backgroundColor: MuiDarkTheme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor:
                    // @ts-expect-error TODO: update the encore-mui types
                    MuiDarkTheme.palette.primary._states.selected,
                  // Active state: text and icons should be white
                  '& .MuiTypography-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                  '& .MuiSvgIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                  '&:hover': {
                    // @ts-expect-error TODO: update the encore-mui types
                    backgroundColor: MuiDarkTheme.palette.primary._states.hover,
                  },
                },
              },
              // Footer area overrides
              '& .EncoreSidebarFooter-root': {
                '& .MuiListItemIcon-root': {
                  minWidth: 34,
                  color: MuiDarkTheme.palette.text.secondary,
                },
              },
              '& .MuiListItemIcon-root': {
                // Inactive icons use secondary as well to match text
                color: MuiDarkTheme.palette.text.secondary,
              },
              '& .MuiListItemButton-root .MuiTypography-root': {
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
                // Inactive label color should be secondary
                color: MuiDarkTheme.palette.text.secondary,
              },
              // Mini drawer labels (caption variant) should stay compact
              '& .MuiDrawer-paper .MuiListItemButton-root .MuiTypography-caption':
                {
                  fontSize: '9px',
                  lineHeight: '12px',
                },
              // Active state label and icon to white (reinforce, in case of specificity)
              '& .MuiListItemButton-root.Mui-selected .MuiTypography-root': {
                color: MuiDarkTheme.palette.common.white,
              },
              '& .MuiListItemButton-root.Mui-selected .MuiSvgIcon-root': {
                color: MuiDarkTheme.palette.common.white,
              },
              '& .MuiDivider-root': {
                borderColor: MuiDarkTheme.palette.divider,
              },
              // Sidebar collapsed flyout scroll behavior (scoped to drawer)
              '& .MuiListItem-root > .MuiBox-root .MuiPaper-root': {
                maxHeight: 'calc(100vh - 96px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarColor: `${MuiDarkTheme.palette.divider} transparent`,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: 8,
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  borderRadius: 999,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: MuiDarkTheme.palette.divider,
                },
                backgroundColor: MuiDarkTheme.palette.background.paper,
                color: MuiDarkTheme.palette.text.primary,
              },
              '& .MuiListItem-root > .MuiBox-root .MuiPaper-root .MuiList-root':
                {
                  maxHeight: 'inherit',
                  overflowY: 'auto',
                  scrollbarColor: `${MuiDarkTheme.palette.divider} transparent`,
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    width: 8,
                    height: 8,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: 999,
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: MuiDarkTheme.palette.divider,
                  },
                },
              // Bold text for parent (non-leaf) navigation items
              '& .MuiListItem-root:has(+ .MuiCollapse-root) > .MuiListItemButton-root .MuiTypography-root':
                {
                  fontWeight: 600,
                },
              // Highlight parent modules when any descendant is selected (supports nested modules)
              // Do NOT apply background here (applies when expanded). Background is applied only when collapsed via class below.
              '& .MuiListItem-root:has(.MuiListItemButton-root.Mui-selected) > .MuiListItemButton-root':
                {
                  // Elevate parent visuals to active: ensure text/icons use primary text color
                  color: MuiDarkTheme.palette.text.primary,
                  '& .MuiTypography-root, & .MuiSvgIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                  '& .MuiListItemIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                  '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                },
              // DOM-independent: apply visuals based on expanded/collapsed classes
              '& .MuiListItemButton-root.encore-parent-active.encore-parent-collapsed':
                {
                  backgroundColor:
                    // @ts-expect-error TODO: update the encore-mui types
                    MuiDarkTheme.palette.primary._states.selected,
                  '&:hover': {
                    // @ts-expect-error TODO: update the encore-mui types
                    backgroundColor: MuiDarkTheme.palette.primary._states.hover,
                  },
                  color: MuiDarkTheme.palette.text.primary,
                  '& .MuiTypography-root, & .MuiSvgIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                  '& .MuiListItemIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                },
              // When expanded, keep text/icon color but do not force selected background
              '& .MuiListItemButton-root.encore-parent-active.encore-parent-expanded':
                {
                  color: MuiDarkTheme.palette.text.primary,
                  '& .MuiTypography-root, & .MuiSvgIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                  '& .MuiListItemIcon-root': {
                    color: MuiDarkTheme.palette.text.primary,
                  },
                },
              '& .MuiListItem-root': {
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            },
            // Collapsed (mini) drawer: ensure selected state remains visually active
            '&.MuiDrawer-docked.MuiDrawer-paperAnchorLeft .MuiListItemButton-root.Mui-selected':
              {
                // Keep the same selected background, but ensure icon uses white
                '& .MuiSvgIcon-root': {
                  color: MuiDarkTheme.palette.common.white,
                },
              },
          },
        },
      },
      // Dark theme for AppBar (header/toolbar)
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: MuiDarkTheme.palette.background.paper,
            color: MuiDarkTheme.palette.text.primary,
            borderBottom: `1px solid ${MuiDarkTheme.palette.divider} !important`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingLeft: '16px !important',
            '@media (min-width: 600px)': {
              minHeight: '56px',
            },
            color: MuiDarkTheme.palette.text.primary,
            '& .MuiIconButton-root': {
              color: MuiDarkTheme.palette.text.primary,
            },
            '& .MuiTypography-root': {
              color: MuiDarkTheme.palette.text.primary,
            },
          },
        },
      },
    },
  });
};
